import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CalibrationBalance, CalibrationBalanceAudit, CalibrationBalanceAuditFilter } from '../../../../model/calibrationBalance';
import { InternalEquipment } from 'src/app/model/internalEquipment';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { merge, Subject, takeUntil, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CalibrationBalanceService } from '../../../../services/calibrationBalance.service';
import { SnackBarService } from '../../../../services/snackBar.service';
import { SpinnerService } from '../../../../services/spinner.service';
import { CalibrationBalanceUtils } from '../../../../utils/calibrationBalanceUtils';
import { saveAs } from 'file-saver';
import { CalibrationBalanceAuditDataSource } from '../../../../model/calibrationBalanceAuditDataSource';

@Component({
  selector: 'app-calibration-balance-edit-audit',
  templateUrl: './calibration-balance-edit-audit.component.html',
  styleUrl: './calibration-balance-edit-audit.component.scss',
  standalone: false
})
export class CalibrationBalanceEditAuditComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() idCalibration: number;
  @Input() documentCode: string;
  @Input() calibration: CalibrationBalance;
  @Input() equipment: InternalEquipment;

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  public filter: CalibrationBalanceAuditFilter;

  public displayedColumns = ['date', 'username', 'action', 'status', 'currentVersion', 'signedVersion', 'changes', 'download'];
  public dataSource: CalibrationBalanceAuditDataSource;

  private destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private calibrationService: CalibrationBalanceService,
    public snackBarService: SnackBarService,
    private spinnerService: SpinnerService) {
    this.spinnerService.show();
  }

  ngOnInit(): void {
    this.filter = new CalibrationBalanceAuditFilter();
    this.getAudit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // reset the paginator after sorting
    this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page).pipe(tap(() => this.getAudit())).pipe(takeUntil(this.destroy$)).subscribe();
  }

  getAudit(): void {

    this.dataSource = new CalibrationBalanceAuditDataSource(this.calibrationService);

    this.filter.sortBy = this.sort.active || 'date';
    this.filter.sortDirection = this.sort.direction || 'asc';
    this.filter.pageIndex = this.paginator.pageIndex || 0;
    this.filter.pageSize = this.paginator.pageSize || 10;

    this.dataSource.loadCalibrationAudit(this.idCalibration, this.filter);
    this.dataSource.loading$.pipe(takeUntil(this.destroy$)).subscribe(message => this.spinnerService.next(message));

  }

  downloadAuditPdf(): void {
    this.spinnerService.show();

    this.calibrationService.downloadAuditPdf(this.idCalibration).pipe(takeUntil(this.destroy$)).subscribe(
      (res: Blob) => saveAs(res, this.translate.instant('calibrateEquipmentEdit.files.audit', { name: this.documentCode }) as string),
      error => console.error(error),
      () => this.spinnerService.hide());
  }

  showDownloadSignedPdf(calibration: CalibrationBalanceAudit): boolean {
    return calibration.canDownload;
  }

  downloadSignedPdf(calibration: CalibrationBalanceAudit): void {

    this.spinnerService.show();

    this.calibrationService.downloadPdfVersion(this.idCalibration, calibration.signedVersion).pipe(takeUntil(this.destroy$))
      .subscribe((res: Blob) => {
        const name = this.getNameForCalibrationAudit();
        saveAs(res, name);
        this.spinnerService.hide();
      }, () => {
        this.snackBarService.sendError(
          this.translate.instant('calibrateEquipmentEdit.dialog.generateReport.form.error.notFound') as string);
        this.spinnerService.hide();
      });
  }

  private getNameForCalibrationAudit(): string {
    return CalibrationBalanceUtils.getNameForCalibration(this.calibration, this.equipment, this.translate);
  }

}

